import {
  animate,
  AnimationEvent,
  query,
  transition,
  trigger,
} from '@angular/animations';
import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { noop } from '@rivethealth/core/util';
import { ObservableInput } from 'observable-input';
import {
  combineLatest,
  concat,
  Observable,
  of,
  Subject,
  Unsubscribable,
} from 'rxjs';
import { delay, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { ErrorBoundaryService } from '../core/error-boundary.service';
import { EventsService } from '../core/events.service';
import { ToastService } from '../notification/toast.service';

enum AnimationEvent_ {
  START,
  STOP,
}

enum AnimationPhase {
  ENTER = 'enter',
  MIDPOINT = 'midpoint',
  LEAVE = 'leave',
}

const animationDuration = 400;

@Component({
  animations: [
    // TODO: handle multiple quick routing, e.g. with
    // https://www.bennadel.com/blog/3219-using-animation-callbacks-when-animation-transitions-are-interrupted-in-angular-2-4-4.htm
    trigger('fade', [
      transition('void <=> *', []),
      transition('* <=> *', [
        // prevent child routes from disappearing
        // https://github.com/angular/angular/issues/15477
        // cannot change display with animation API, so we perform the animation elsewhere
        // https://github.com/angular/angular/issues/27577
        query('router-outlet ~ *', animate('{{duration}}ms')),
      ]),
    ]),
  ],
  host: { class: 'rv-rows' },
  // <error-boundary> didn't work; perhaps due to animation?
  providers: [ErrorBoundaryService],
  selector: 'fading-router-outlet',
  templateUrl: './fading-router-outlet.component.html',
})
export class FadingRouterOutletComponent {
  counter = 0;
  private readonly routes = new WeakMap();

  @HostBinding('@fade')
  get state() {
    if (!this.outlet || !this.outlet.isActivated) {
      return 'void';
    }
    if (!this.routes.has(this.outlet.activatedRoute.routeConfig!)) {
      this.routes.set(this.outlet.activatedRoute.routeConfig!, this.counter++);
    }
    return {
      value: this.routes.get(this.outlet.activatedRoute.routeConfig!),
      params: { duration: animationDuration },
    };
  }

  error: any;

  @ViewChild('errorToast', { static: false })
  errorToast: TemplateRef<{}>;

  @ViewChild(RouterOutlet, { static: false })
  outlet: RouterOutlet;

  @ViewChild('outlet', { read: ElementRef, static: false })
  @ObservableInput()
  outletElement!: Observable<ElementRef<HTMLElement>>;

  private readonly animationEvent = new Subject<AnimationEvent_>();

  private readonly animationPhase = combineLatest([
    this.outletElement,
    this.animationEvent.pipe(
      switchMap(event => {
        switch (event) {
          case AnimationEvent_.START:
            return concat(
              of(AnimationPhase.LEAVE),
              of(AnimationPhase.MIDPOINT).pipe(delay(animationDuration / 2)),
              of(AnimationPhase.ENTER),
            );
          case AnimationEvent_.STOP:
            return of(undefined);
        }
      }),
      distinctUntilChanged(),
    ),
  ]).pipe(
    tap(([element, phase]) => {
      noop(element.nativeElement.offsetHeight);
      element.nativeElement.classList.remove(
        AnimationPhase.ENTER,
        AnimationPhase.MIDPOINT,
        AnimationPhase.LEAVE,
      );
      if (phase !== undefined) {
        element.nativeElement.classList.add(phase);
      } else {
        this.eventService.doResize.next(undefined);
      }
      noop(element.nativeElement.offsetHeight);
      if (phase === AnimationPhase.ENTER) {
        this.eventService.doResize.next(undefined);
      }
    }),
  );
  private animationPhaseSubscription: Unsubscribable;

  ngDoCheck() {
    this.error = this.errorBoundaryService.ngDoCheck();
    if (this.error && this.errorToast) {
      this.toastService.toast.next({
        template: this.errorToast,
        duration: 1000 * 3,
      });
    }
  }

  @HostListener('@fade.start', ['$event'])
  startAnimation(event: AnimationEvent) {
    if (event.fromState !== 'void') {
      this.animationEvent.next(AnimationEvent_.START);
    }
  }

  @HostListener('@fade.done')
  doneAnimation() {
    this.animationEvent.next(AnimationEvent_.STOP);
  }

  constructor(
    private readonly toastService: ToastService,
    private readonly eventService: EventsService,
    @Self() private readonly errorBoundaryService: ErrorBoundaryService,
  ) {}

  ngOnDestroy() {
    if (this.animationPhaseSubscription) {
      this.animationPhaseSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.animationPhaseSubscription = this.animationPhase.subscribe();
  }
}
